import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Mail } from "lucide-react";
import Logo from "../../../assets/Logo.svg";
import comingSoonHero from "../../../assets/images/coming-soon.jpg";
import styles from "./RegisterComingSoonPage.module.css";

const STORAGE_KEY = "sugarcribs_notify_emails";

const RegisterComingSoonPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [hasError, setHasError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmed || !emailRegex.test(trimmed)) {
      setHasError(true);
      return;
    }

    setHasError(false);

    setIsSubmitting(true);
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (!saved.includes(trimmed)) {
        saved.push(trimmed);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
      }

      // Klaviyo subscription integration
      const KLAVIYO_SITE_ID = process.env.REACT_APP_KLAVIYO_SITE_ID || "YOUR_SITE_ID";
      const KLAVIYO_LIST_ID = process.env.REACT_APP_KLAVIYO_LIST_ID || "YOUR_LIST_ID";

      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_SITE_ID}`, {
        method: "POST",
        headers: {
          "revision": "2024-02-15",
          "content-type": "application/json",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Coming Soon Page",
              profile: {
                data: {
                  type: "profile",
                  attributes: { email: trimmed },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: "list",
                  id: KLAVIYO_LIST_ID,
                },
              },
            },
          },
        }),
      }).catch(() => {
        /* silently ignored — error handling TODO */
      });

      setIsSubmitted(true);
      setEmail("");
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Helmet>
        <title>SugarCribs is Coming Soon</title>
        <meta
          name="description"
          content="Be the first to know when SugarCribs goes live. Join our community and get exclusive updates, early access and more."
        />
      </Helmet>

      {/* Top Navbar */}
      <header className={styles.header}>
        <Link to="/" className={styles.logoLink} aria-label="SugarCribs Home">
          <img src={Logo} alt="SugarCribs" className={styles.logo} />
        </Link>
      </header>

      {/* Main Hero Section */}
      <main className={styles.heroSection}>
        {/* Left Column: Copy & Form */}
        <div className={styles.contentColumn}>
          <div className={styles.tagline}>
            <span>EXCLUSIVE</span>
            <span>&gt;</span>
            <span>PREMIUM</span>
            <span>&gt;</span>
            <span>COMMUNITY</span>
          </div>

          <h1 className={styles.title}>SugarCribs is Coming Soon</h1>

          <p className={styles.description}>
            Be the first to know when we go live. Join our community and get
            exclusive updates, early access and more.
          </p>

          <div className={styles.emailForm}>
            {isSubmitted ? (
              <div className={styles.successCard}>
                <div className={styles.successIconWrapper}>
                  <div className={styles.successCheckBadge}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                        stroke="#000000"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <div className={styles.successTextWrapper}>
                  <h3 className={styles.successTitle}>You’re All Set!</h3>
                  <p className={styles.successSubtitle}>
                    Thanks for subscribing. We’ll keep you updated with the latest news and announcements.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <form noValidate onSubmit={handleSubmit} className={styles.formContainer}>
                  <div className={`${styles.emailInputWrapper} ${hasError ? styles.emailInputWrapperError : ""}`}>
                    {!hasError && <Mail size={18} className={styles.emailIcon} />}
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (hasError) setHasError(false);
                      }}
                      placeholder="Enter your email address"
                      className={styles.emailInput}
                      disabled={isSubmitting}
                      aria-label="Email address"
                    />
                    {hasError ? (
                      <div className={styles.errorIconWrapper}>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="12" cy="12" r="10" stroke="#FF0000" strokeWidth="2" />
                          <path d="M12 7V13" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" />
                          <circle cx="12" cy="17" r="1.2" fill="#FF0000" />
                        </svg>
                      </div>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={styles.notifyButton}
                      >
                        {isSubmitting ? "Saving..." : "Notify Me"}
                      </button>
                    )}
                  </div>

                  {hasError && (
                    <p className={styles.errorText}>
                      We respect your privacy. No spam. ever.
                    </p>
                  )}
                </form>

                <div className={styles.privacyNote}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.75 8V9.25H17.33C17.865 9.25 18.31 9.25 18.675 9.28C19.055 9.311 19.411 9.378 19.748 9.55C20.2656 9.81367 20.6863 10.2344 20.95 10.752C21.122 11.089 21.19 11.446 21.22 11.826C21.25 12.19 21.25 12.636 21.25 13.17V17.83C21.25 18.365 21.25 18.81 21.22 19.175C21.19 19.555 21.122 19.912 20.95 20.249C20.6866 20.7664 20.2662 21.1872 19.749 21.451C19.411 21.623 19.055 21.69 18.675 21.721C18.31 21.751 17.865 21.751 17.331 21.751H6.67C6.135 21.751 5.69 21.751 5.325 21.721C4.945 21.69 4.589 21.623 4.252 21.451C3.73445 21.1873 3.31367 20.7666 3.05 20.249C2.878 19.912 2.81 19.555 2.78 19.175C2.75 18.811 2.75 18.365 2.75 17.831V13.17C2.75 12.636 2.75 12.19 2.78 11.826C2.81 11.446 2.878 11.089 3.05 10.752C3.31367 10.2344 3.73445 9.81367 4.252 9.55C4.589 9.378 4.945 9.311 5.325 9.28C5.69 9.25 6.135 9.25 6.67 9.25H7.25V8C7.25 6.522 7.58 5.099 8.357 4.025C9.157 2.918 10.387 2.25 12 2.25C13.613 2.25 14.842 2.918 15.643 4.025C16.42 5.099 16.75 6.522 16.75 8ZM8.75 8V9.25H15.25V8C15.25 6.717 14.96 5.64 14.428 4.904C13.918 4.201 13.148 3.75 12 3.75C10.852 3.75 10.081 4.2 9.572 4.904C9.04 5.64 8.75 6.717 8.75 8ZM16.75 14C16.75 13.8011 16.671 13.6103 16.5303 13.4697C16.3897 13.329 16.1989 13.25 16 13.25C15.8011 13.25 15.6103 13.329 15.4697 13.4697C15.329 13.6103 15.25 13.8011 15.25 14V17C15.25 17.1989 15.329 17.3897 15.4697 17.5303C15.6103 17.671 15.8011 17.75 16 17.75C16.1989 17.75 16.3897 17.671 16.5303 17.5303C16.671 17.3897 16.75 17.1989 16.75 17V14Z" fill="white"/>
                  </svg>
                  <span>We respect your privacy. No spam. ever.</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right-Side Hero Image with Gradient Overlays & Figma Blur Layer */}
        <div className={styles.imageWrapper}>
          <img
            src={comingSoonHero}
            alt="SugarCribs Creator Experience"
            className={styles.heroImage}
          />
          {/* Deep dark gradient shadow overlay matching Figma */}
          <div className={styles.imageGradientOverlay} />
          {/* Figma Ellipse 86: Atmospheric Pink Glow / Layer Blur (#FF78AA) */}
          <div className={styles.blurLayer} />
        </div>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        {/* <div className={styles.footerLogoWrapper}>
          <Link to="/" className={styles.logoLink} aria-label="SugarCribs Home">
            <img src={Logo} alt="SugarCribs" className={styles.footerLogo} />
          </Link>
        </div> */}

        <p className={styles.footerCopyright}>
          © 2026 SugarCribs. All rights reserved.
        </p>

        <div className={styles.footerLinks}>
          <Link to="/terms-of-service" className={styles.footerLink}>
            Terms of Service
          </Link>
          <span className={styles.footerSeparator}>•</span>
          <Link to="/privacy-policy" className={styles.footerLink}>
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default RegisterComingSoonPage;
